import React, { useState, useEffect, startTransition } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { login, registerInitiate } from '@/api/auth';
import apiClient from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import type { UserRole } from '@/types';
import { setPostAuthRedirect, consumePostAuthRedirect } from '@/utils/postAuthRedirect';
import { getPendingInvite, clearPendingInvite } from '@/utils/referralCapture';

type AuthTab = 'login' | 'register';

/* ==================== 利润自测宣传轮播（v2.0.48） ==================== */

const QUIZ_SLOGANS = [
  '你的利润，正在从哪里漏走？10 道题 · 3 分钟，定位 5 大利润漏点',
  '营收在涨、利润没跟上？免费测一测你的利润健康度',
  '接单靠感觉、成本算不清？一次自测看清经营的隐性失血点',
  '阿米巴经营第一步：先看清利润漏点，再谈降本增效',
];

const ProfitQuizBanner: React.FC = () => {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIdx((prev) => (prev + 1) % QUIZ_SLOGANS.length);
    }, 4500);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <Box
      sx={{
        mb: 2,
        px: 2,
        py: 1.5,
        borderRadius: 2,
        background: 'linear-gradient(135deg, #1A365D 0%, #2A4A7F 60%, #3182CE 100%)',
        color: '#fff',
      }}
    >
      <Box sx={{ minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography
          key={idx}
          variant="body2"
          sx={{
            fontWeight: 600,
            lineHeight: 1.5,
            animation: 'quizSloganFade 600ms ease',
            '@keyframes quizSloganFade': {
              from: { opacity: 0, transform: 'translateY(6px)' },
              to: { opacity: 1, transform: 'none' },
            },
          }}
        >
          {QUIZ_SLOGANS[idx]}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mt: 1 }}>
        <Box sx={{ display: 'flex', gap: 0.6 }}>
          {QUIZ_SLOGANS.map((s, i) => (
            <Box
              key={s}
              onClick={() => setIdx(i)}
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                cursor: 'pointer',
                bgcolor: i === idx ? '#F6C453' : 'rgba(255,255,255,0.45)',
                transition: 'background-color 200ms ease',
              }}
            />
          ))}
        </Box>
        <Button
          size="small"
          variant="contained"
          // 懒加载页导航须用 startTransition：同步点击中首次加载 chunk 会 suspend，抛 React #426 被错误边界兜成整页错误
          onClick={() => startTransition(() => navigate('/profit-quiz'))}
          sx={{
            py: 0.2,
            px: 1.5,
            fontSize: 12,
            bgcolor: '#F6C453',
            color: '#1A365D',
            fontWeight: 700,
            '&:hover': { bgcolor: '#E8B63C' },
          }}
        >
          立即测一测
        </Button>
      </Box>
    </Box>
  );
};

/** 把全角数字/字母统一转成半角，避免中文输入法导致 parseInt 失败 */
function normalizeFullWidth(input: string): string {
  return input
    .replace(/[\uFF10-\uFF19]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0))
    .replace(/[\uFF21-\uFF3A]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0))
    .replace(/[\uFF41-\uFF5A]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0));
}

/** 登录/注册后处理租户切换 */
async function handlePostAuth(
  res: { data: { user: any; token: string; tenants: any[]; isConsultant?: boolean; isPlatformAdmin?: boolean; isChannel?: boolean; consultantStatus?: 'active' | 'pending' | 'disabled' | null; certStatus?: 'approved' | 'pending' | 'rejected' | null } },
  helpers: {
    setToken: (t: string) => void;
    setUser: (u: any) => void;
    setCurrentTenant: (t: any, r: UserRole) => void;
    setConsultant: (v: boolean) => void;
    setConsultantStatus: (v: 'active' | 'pending' | 'disabled' | null) => void;
    setCertStatus: (v: 'approved' | 'pending' | 'rejected' | null) => void;
    setIsPlatformAdmin: (v: boolean) => void;
    setIsChannel: (v: boolean) => void;
    setIdentity: (v: string | null) => void;
    navigate: (path: string) => void;
  }
) {
  const { user, token, tenants, isConsultant, isPlatformAdmin, isChannel, consultantStatus, certStatus } = res.data;
  helpers.setToken(token);
  helpers.setUser(user);
  helpers.setConsultant(!!isConsultant);
  helpers.setConsultantStatus(consultantStatus ?? null);
  helpers.setCertStatus(certStatus ?? null);
  helpers.setIsPlatformAdmin(!!isPlatformAdmin);
  helpers.setIsChannel(!!isChannel);
  helpers.setIdentity(user?.identity ?? null);

  if (tenants.length === 1) {
    const tenant = tenants[0];
    try {
      const switchRes = await apiClient.post(`/tenants/${tenant.id}/switch`);
      const { token: newToken, role, isPlatformAdmin: swIsPlatformAdmin, isChannel: swIsChannel } = switchRes.data.data;
      helpers.setToken(newToken);
      helpers.setCurrentTenant(tenant, role);
      helpers.setIsPlatformAdmin(!!swIsPlatformAdmin);
      helpers.setIsChannel(!!swIsChannel);
      // v2.0.48：从公开页（如利润自测）跳来注册/登录的，登录成功后回跳原页面；
      // 否则登录后先进闪屏（启动页），再进首页。
      const redirect = consumePostAuthRedirect();
      // v2.2.73：邀请兜底一次性消费——认证成功即清除 pending invite，
      // 否则 stale redirect 会在之后每次登录都把老用户拽回分享页（如 /profit-quiz）。
      // 修复 2026-10-18 用户反馈「每次登录都要做利润自测」。
      clearPendingInvite();
      helpers.navigate(redirect || '/splash');
    } catch {
      helpers.navigate('/tenant-select');
    }
  } else {
    helpers.navigate('/tenant-select');
  }
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setToken, setUser, setCurrentTenant, setConsultant, setConsultantStatus, setCertStatus, setIsPlatformAdmin, setIsChannel, setIdentity: setIdentityStore } = useAuthStore();

  const [tab, setTab] = useState<AuthTab>('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  // 方案 A：微信静默登录失败回落时守卫透传的真实错误（此前被吞，排查全靠猜）
  const [wxLoginError, setWxLoginError] = useState('');
  // v2.0.48：是否从利润自测页跳转而来（决定顶部展示轮播还是回跳提示）
  const [fromQuiz, setFromQuiz] = useState(false);
  // 微信扫码绑定结果：回调先落 /settings/points?wxbind=..&msg=..，手机 webview 无登录态
  // 被路由守卫带到本页（参数由 ProtectedRoute 透传）。不展示的话用户只会看到登录页，
  // 完全无从判断绑定成功还是失败（2026-09-27 实测踩坑）
  const [wxBind, setWxBind] = useState<{ ok: boolean; msg: string } | null>(null);

  // 邀请码 + UTM（来自分享链接 /login?invite=CODE&utm_*=...）
  const [inviteInfo, setInviteInfo] = useState<{
    code: string;
    utmSource: string;
    utmMedium: string;
    utmCampaign: string;
    utmTerm: string;
    utmContent: string;
  } | null>(null);
  // 邀请人脱敏名（公开解析，无需登录）
  const [inviteConsultantName, setInviteConsultantName] = useState<string | null>(null);
  // v2.0.54 企业成员邀请（/login?join=J-XXXXXXXX）：注册后加入既有企业，不新建
  const [joinCode, setJoinCode] = useState<string>('');
  const [joinInfo, setJoinInfo] = useState<{
    tenantName: string;
    role: 'member' | 'tenant_admin';
  } | null>(null);
  const [joinError, setJoinError] = useState<string>('');
  // 平台公告（公开拉取，无需登录；失败静默不阻塞登录）
  const [announcements, setAnnouncements] = useState<
    { id: string; title: string; content: string; type: 'info' | 'warning' | 'error' | 'success' }[]
  >([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { announcementApi } = await import('@/api/announcement');
        const items = await announcementApi.listActive();
        if (!cancelled && Array.isArray(items)) {
          setAnnouncements(items);
        }
      } catch {
        /* 非致命：公告拉取失败不阻塞登录 */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!inviteInfo) return;
    let cancelled = false;
    (async () => {
      try {
        const { resolveInviteCode } = await import('@/api/consultantInvite');
        const r = await resolveInviteCode(inviteInfo.code);
        if (!cancelled && r.data?.valid) {
          setInviteConsultantName(r.data.consultantNameMasked || null);
        }
      } catch {
        /* 非致命：解析失败也不阻塞注册 */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteInfo]);

  // 首次挂载：解析 URL 参数，命中邀请码则自动切到注册页
  useEffect(() => {
    const pending = getPendingInvite();
    const code = pending?.code || '';
    const utmSource = pending?.utmSource || '';
    const utmMedium = pending?.utmMedium || '';
    const utmCampaign = pending?.utmCampaign || '';
    const utmTerm = pending?.utmTerm || '';
    const utmContent = pending?.utmContent || '';
    const join = (searchParams.get('join') || '').trim().toUpperCase();
    if (code) {
      setInviteInfo({ code, utmSource, utmMedium, utmCampaign, utmTerm, utmContent });
      setTab('register');
      // 全局分享：注册成功后回跳到分享人所在的页面（让"分享当前页面"名副其实）
      if (pending?.redirect) {
        setPostAuthRedirect(pending.redirect);
        setFromQuiz(pending.redirect.indexOf('/profit-quiz') === 0);
      }
    } else if (join || searchParams.get('tab') === 'register') {
      setTab('register');
    }
    if (join) {
      setJoinCode(join);
    }
    // v2.0.48：记录登录/注册成功后的回跳目标（如利润自测结果页）
    const redirect = searchParams.get('redirect') || '';
    if (redirect) {
      setPostAuthRedirect(redirect);
      setFromQuiz(redirect.indexOf('/profit-quiz') === 0);
    }
    // 仅在挂载时解析一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 微信扫码绑定结果：接住守卫透传的 wxbind/msg，展示后立刻清掉 URL 参数（避免刷新重复弹）
  useEffect(() => {
    if (!searchParams.has('wxbind')) return;
    setWxBind({
      ok: searchParams.get('wxbind') === '1',
      msg: searchParams.get('msg') || '',
    });
    const url = new URL(window.location.href);
    ['wxbind', 'msg'].forEach((k) => url.searchParams.delete(k));
    window.history.replaceState({}, '', url.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 方案 A：微信静默登录失败 —— 展示真实错误后清参（避免刷新重复弹）
  useEffect(() => {
    if (!searchParams.has('wx_login_error')) return;
    setWxLoginError(searchParams.get('wx_login_error') || '微信快捷登录未完成');
    const url = new URL(window.location.href);
    url.searchParams.delete('wx_login_error');
    window.history.replaceState({}, '', url.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -- 登录表单 --
  const [account, setAccount] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // -- 注册表单 --
  const [companyName, setCompanyName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [captcha, setCaptcha] = useState(() => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    return { a, b, expected: a + b };
  });
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [registerInitiated, setRegisterInitiated] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // 注册身份：企业主 / 企业高管 / 咨询师 / 其他（咨询师需平台管理员审核通过后才享受分润与提交权限）
  const [identity, setIdentity] = useState<'owner' | 'executive' | 'consultant' | 'other'>('owner');

  // v2.0.54：解析企业成员邀请码，成功则锁定企业名（注册后直接加入该企业，不新建）
  useEffect(() => {
    if (!joinCode) return;
    let cancelled = false;
    (async () => {
      try {
        const { resolveTenantInvite } = await import('@/api/tenantInvitation');
        const r = await resolveTenantInvite(joinCode);
        if (cancelled) return;
        const d = r.data;
        if (d && d.valid) {
          setJoinInfo({
            tenantName: d.tenantName || '',
            role: d.role === 'tenant_admin' ? 'tenant_admin' : 'member',
          });
          setCompanyName(d.tenantName || '');
          setIdentity('other');
          setJoinError('');
        } else {
          const reasonMap: Record<string, string> = {
            not_found: '邀请链接无效',
            revoked: '该邀请链接已被撤销',
            expired: '该邀请链接已过期',
            quota_exceeded: '该邀请链接使用次数已达上限',
          };
          setJoinInfo(null);
          setJoinError(
            (reasonMap[(d && d.reason) || 'not_found'] || '邀请链接无效') +
              '，请向企业管理员索取新链接（也可继续以新建企业的方式注册）'
          );
        }
      } catch {
        /* 非致命：解析失败按普通注册处理 */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [joinCode]);

  const regenerateCaptcha = () => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    setCaptcha({ a, b, expected: a + b });
    setCaptchaAnswer('');
  };

  const handleTabChange = (_: React.SyntheticEvent, newTab: AuthTab) => {
    setTab(newTab);
    setError('');
    setSuccessMsg('');
    if (newTab === 'register') {
      regenerateCaptcha();
    }
  };

  // ========== 登录处理 ==========
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!account.trim() || !loginPassword.trim()) {
      setError('请输入账号和密码');
      return;
    }

    const trimmed = account.trim();
    const isEmail = trimmed.includes('@');
    const payload = isEmail
      ? { email: trimmed, password: loginPassword }
      : { phone: trimmed, password: loginPassword };

    setLoading(true);
    try {
      const res = await login(payload);
      await handlePostAuth(res, { setToken, setUser, setCurrentTenant, setConsultant, setConsultantStatus, setCertStatus, setIsPlatformAdmin, setIsChannel, setIdentity: setIdentityStore, navigate });
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || '登录失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // ========== 注册处理（链接验证） ==========
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    if (!companyName.trim() || !regEmail.trim() || !regPassword.trim() || !displayName.trim()) {
      setError('请填写所有必填项（公司名称、邮箱、密码、姓名）');
      return;
    }

    if (!agreedToTerms) {
      setError('请阅读并同意用户服务协议和隐私政策');
      return;
    }

    const normalizedAnswer = normalizeFullWidth(captchaAnswer.trim());
    const parsed = parseInt(normalizedAnswer, 10);
    if (!normalizedAnswer || Number.isNaN(parsed) || parsed !== captcha.expected) {
      setError(`安全验证答案错误（正确应为 ${captcha.expected}）`);
      regenerateCaptcha();
      return;
    }

      setLoading(true);
      try {
        const res = await registerInitiate({
          companyName: companyName.trim(),
          email: regEmail.trim(),
          password: regPassword,
          displayName: displayName.trim(),
          phone: regPhone.trim() || undefined,
          identity,
          invite: inviteInfo?.code || undefined,
          tenantInvite: joinInfo ? joinCode : undefined,
          utmSource: inviteInfo?.utmSource || undefined,
          utmMedium: inviteInfo?.utmMedium || undefined,
          utmCampaign: inviteInfo?.utmCampaign || undefined,
          utmTerm: inviteInfo?.utmTerm || undefined,
          utmContent: inviteInfo?.utmContent || undefined,
        });
      setRegisterInitiated(true);
      // 注册请求已提交：后端会发送验证邮件（SMTP 配置后）或在控制台输出调试链接
      setSuccessMsg(
        (joinInfo
          ? `注册申请已提交，验证通过后你将以「${joinInfo.role === 'tenant_admin' ? '企业管理员' : '普通成员'}」身份加入「${joinInfo.tenantName}」。请检查邮箱并点击验证链接完成注册。`
          : inviteInfo
          ? `注册申请已提交（邀请码 ${inviteInfo.code}）。请检查邮箱并点击验证链接完成注册（未收到邮件时请联系管理员）。`
          : '注册申请已提交。请检查邮箱并点击验证链接完成注册（未收到邮件时请联系管理员）。') +
          (fromQuiz ? '完成验证并登录后，将自动回到利润自测结果页展示完整诊断报告。' : '')
      );
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || '注册失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {fromQuiz ? (
        <Alert severity="info" variant="outlined" sx={{ mb: 2, textAlign: 'left' }}>
          你的<b>利润自测答卷已保存</b>。完成注册并登录后，将自动回到结果页展示完整诊断报告。
        </Alert>
      ) : (
        <ProfitQuizBanner />
      )}
      {wxBind && (
        <Alert
          severity={wxBind.ok ? 'success' : 'warning'}
          sx={{ mb: 2, alignItems: 'flex-start', '& .MuiAlert-message': { width: '100%' } }}
          onClose={() => setWxBind(null)}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            {wxBind.ok ? '微信收款号绑定成功' : '微信绑定未完成'}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {wxBind.ok
              ? '回到电脑端刷新「收款账号」页即可看到；之后提现打款将自动沿用该微信。'
              : (wxBind.msg || '授权未完成') + '。可在电脑端「收款账号」页重新扫码，或用弹窗里的「在微信中打开」。'}
          </Typography>
        </Alert>
      )}
      {wxLoginError && (
        <Alert
          severity="warning"
          sx={{ mb: 2, alignItems: 'flex-start', '& .MuiAlert-message': { width: '100%' } }}
          onClose={() => setWxLoginError('')}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            微信快捷登录未完成
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {wxLoginError}。可退出聊天重新扫码进入，或直接用邮箱 / 手机号登录。
          </Typography>
        </Alert>
      )}
      {announcements.map((a) => (
        <Alert
          key={a.id}
          severity={a.type}
          variant="outlined"
          sx={{ mb: 2, alignItems: 'flex-start', '& .MuiAlert-message': { width: '100%' } }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            {a.title}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {a.content}
          </Typography>
        </Alert>
      ))}
      <Tabs
        value={tab}
        onChange={handleTabChange}
        centered
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="登录" value="login" />
        <Tab label="注册" value="register" />
      </Tabs>

      {error && (
        <Alert
          severity="error"
          variant="filled"
          sx={{ mb: 2, alignItems: 'center', '& .MuiAlert-message': { width: '100%' } }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert
          severity="success"
          variant="filled"
          sx={{ mb: 2, alignItems: 'center', '& .MuiAlert-message': { width: '100%' } }}
          onClose={() => setSuccessMsg('')}
        >
          {successMsg}
        </Alert>
      )}
      {inviteInfo && (
        <Alert
          severity="info"
          variant="outlined"
          sx={{ mb: 2, alignItems: 'center', '& .MuiAlert-message': { width: '100%' } }}
          onClose={() => setInviteInfo(null)}
        >
          您正在通过{inviteConsultantName ? `“${inviteConsultantName}”` : `邀请码 ${inviteInfo.code}`}的邀请注册并免费试用 iBusiness 阿米巴经营软件。
        </Alert>
      )}
      {joinInfo && (
        <Alert severity="success" variant="outlined" sx={{ mb: 2, textAlign: 'left' }}>
          您正在受邀加入企业「<b>{joinInfo.tenantName}</b>」，完成注册后将以
          <b>{joinInfo.role === 'tenant_admin' ? '企业管理员' : '普通成员'}</b>
          身份进入该企业（<b>不会新建企业</b>）。
        </Alert>
      )}
      {joinError && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2, textAlign: 'left' }} onClose={() => setJoinError('')}>
          {joinError}
        </Alert>
      )}

      {tab === 'login' ? (
        <Box component="form" onSubmit={handleLogin} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="邮箱 / 手机号"
            value={account}
            onChange={(e) => setAccount(e.target.value)}
            margin="normal"
            size="small"
            placeholder="请输入邮箱或手机号"
          />
          <TextField
            fullWidth
            label="密码"
            type="password"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            margin="normal"
            size="small"
            placeholder="请输入密码"
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ mt: 3, mb: 1, py: 1.2 }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : '登 录'}
          </Button>
          <Box textAlign="center" mt={1}>
            <Typography variant="body2" component={Link} to="/forgot-password" color="text.secondary"
              sx={{ textDecoration: 'none', '&:hover': { color: 'primary.main' } }}>
              忘记密码？
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleRegister} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="公司名称"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            margin="normal"
            size="small"
            required
            placeholder="请输入公司名称"
            disabled={!!joinInfo}
            helperText={joinInfo ? '由邀请链接自动带出，注册后加入该企业' : undefined}
          />
          <TextField
            fullWidth
            label="邮箱"
            type="email"
            value={regEmail}
            onChange={(e) => setRegEmail(e.target.value)}
            margin="normal"
            size="small"
            required
            placeholder="请输入邮箱"
          />
          <TextField
            fullWidth
            label="密码"
            type="password"
            value={regPassword}
            onChange={(e) => setRegPassword(e.target.value)}
            margin="normal"
            size="small"
            required
            placeholder="请设置密码"
          />
          <TextField
            fullWidth
            label="姓名"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            margin="normal"
            size="small"
            required
            placeholder="请输入您的姓名"
          />
          <TextField
            fullWidth
            label="手机号（选填）"
            value={regPhone}
            onChange={(e) => setRegPhone(e.target.value)}
            margin="normal"
            size="small"
            placeholder="请输入手机号（可选）"
          />
          <FormControl size="small" fullWidth margin="normal">
            <InputLabel>我的身份</InputLabel>
            <Select
              label="我的身份"
              value={identity}
              onChange={(e) => setIdentity(e.target.value as 'owner' | 'executive' | 'consultant' | 'other')}
            >
              <MenuItem value="owner">企业主</MenuItem>
              <MenuItem value="executive">企业高管</MenuItem>
              <MenuItem value="consultant">咨询师</MenuItem>
              <MenuItem value="other">其他</MenuItem>
            </Select>
          </FormControl>
          {identity === 'consultant' && (
            <Alert severity="info" variant="outlined" sx={{ mt: 1, mb: 1, alignItems: 'center', '& .MuiAlert-message': { width: '100%' } }}>
              选择「咨询师」后，注册成功仍需平台管理员审核通过，方可享受模板分润与在模板市场提交模块。审核期间可正常使用其他功能。
            </Alert>
          )}
          {/* 安全验证 */}
          <Box display="flex" gap={1} alignItems="flex-start">
            <TextField
              fullWidth
              label={`安全验证：${captcha.a} + ${captcha.b} = ?`}
              value={captchaAnswer}
              onChange={(e) => setCaptchaAnswer(normalizeFullWidth(e.target.value))}
              margin="normal"
              size="small"
              placeholder="请输入答案"
              sx={{ flex: 1 }}
            />
          </Box>
          {/* 用户协议勾选 */}
          <FormControlLabel
            control={
              <Checkbox
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                我已阅读并同意
                <Link to="/terms" target="_blank" style={{ color: '#1a73e8', textDecoration: 'underline' }}>
                  用户服务协议
                </Link>
                和
                <Link to="/privacy" target="_blank" style={{ color: '#1a73e8', textDecoration: 'underline' }}>
                  隐私政策
                </Link>
              </Typography>
            }
            sx={{ mt: 1 }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || registerInitiated}
            sx={{ mt: 3, mb: 2, py: 1.2 }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : '注 册'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Login;
